import React, {useState, useEffect} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import {Add as AddIcon, Save as SaveIcon, Close as CloseIcon} from "@mui/icons-material";
import {RTL} from "../cache/CacheRtl";
import {useAuth} from "../contexts/AuthContext";
import GroupAdminCompaniesField from "./groupadmin/GroupAdminCompaniesField";
import TranslationDialogButton from "./translation/TranslationDialogButton";
import {EquipmentTypes} from "../enums/EquipmentType";
import {SecondaryMuscles} from "../enums/SecondaryMuscles";
import {experienceLevels} from "../enums/ExperienceLevels";
import {TemplateGoals} from "../enums/TemplateGoals";
import {bodyParts} from "../enums/BodyParts";
import {
    MAX_BLOCKS,
    MAX_WEEKS_IN_BLOCK,
    MIN_BLOCKS,
    MIN_WEEKS_IN_BLOCK,
    PERIODIZATION_ERROR_TEXT,
    clampPeriodization,
    maxExceededText
} from "../enums/PeriodizationLimits";

/**
 * field key -> Hebrew label for the translation panel. These are the server's `@TField` keys for
 * NEW_TEMPLATE. Per FIELD, so enabling another locale needs no entry here.
 */
const NEW_TEMPLATE_FIELD_LABELS = {
    name: 'שם התוכנית',
    description: 'תיאור',
    warmupText: 'טקסט חימום',
};

// Enum mappings (you can import these from your existing enum files)
const genderOptions = [
    { value: 'MALE', nameHeb: 'זכר' },
    { value: 'FEMALE', nameHeb: 'נקבה' }
];

const CreateTemplateDialog = ({
                                  open,
                                  onClose,
                                  onSave,
                                  template = null, // If template is provided, we're in edit mode
                                  isEditMode = false // Optional explicit edit mode flag
                              }) => {
    // Determine if we're editing based on template presence or explicit flag
    const editMode = isEditMode || !!template;
    const {isCompanyGroupAdmin} = useAuth();
    const isGroupAdmin = isCompanyGroupAdmin();

    const getInitialFormData = () => {
        if (editMode && template) {
            return {
                id: template.id || null,
                name: template.name || '',
                nameHeb: template.nameHeb || '',
                description: template.description || '',
                descriptionHeb: template.descriptionHeb || '',
                warmupText: template.warmupText || '',
                warmupTextHeb: template.warmupTextHeb || '',
                gender: template.gender || '',
                experienceLevel: template.experienceLevel || '',
                bodyPart: template.bodyPart || '',
                secondaryMuscle: template.secondaryMuscle || undefined,
                secondaryMuscle2: template.secondaryMuscle2 || undefined,
                equipmentType: template.equipmentType || 'GYM',
                enabled: template.enabled !== undefined ? template.enabled : true,
                goals: template.goals || Object.keys(TemplateGoals),
                numberOfBlocks: template.numberOfBlocks || 3,
                numberOfWeeksInBlock: template.numberOfWeeksInBlock || 5,
            };
        }

        // Default values for create mode
        return {
            name: '',
            nameHeb: '',
            description: '',
            descriptionHeb: '',
            // Left blank on create so handleSave sends null and the server seeds the standard
            // warmup copy (WarmupTextDefaults) — matching every pre-existing template.
            warmupText: '',
            warmupTextHeb: '',
            gender: '',
            experienceLevel: '',
            bodyPart: '',
            secondaryMuscle: undefined,
            secondaryMuscle2: undefined,
            equipmentType: 'GYM',
            enabled: true,
            goals: Object.keys(TemplateGoals),
            numberOfBlocks: 3,
            numberOfWeeksInBlock: 5,
        };
    };

    const [formData, setFormData] = useState(getInitialFormData());
    const [errors, setErrors] = useState({});

    // Which periodization fields were just clamped down from an over-limit entry. Separate from
    // `errors`: this is not a validation failure (the value IS valid after clamping) but the user
    // typed 40 and got 20, and needs to be told why rather than watching the field rewrite itself.
    const [clamped, setClamped] = useState({});

    /**
     * Set a periodization field to the clamped value, and note whether clamping actually changed
     * what was typed so the field can explain itself.
     */
    const handlePeriodizationChange = (field, rawValue, max) => {
        const typed = parseInt(rawValue, 10);
        const value = clampPeriodization(rawValue, max, 1);
        setClamped(prev => ({...prev, [field]: !Number.isNaN(typed) && typed > max}));
        handleInputChange(field, value);
    };
    // COMPANY_GROUP_ADMIN only: the authorized companies this program is shared with. Kept out of
    // formData so the super-admin / company-admin create payloads are byte-for-byte unchanged.
    const [groupAdminCompanies, setGroupAdminCompanies] = useState(
        editMode && template ? (template.companyPermissions || []) : []
    );

    // Update form data when template changes (for edit mode)
    useEffect(() => {
        if (editMode && template) {
            setFormData(getInitialFormData());
            setGroupAdminCompanies(template.companyPermissions || []);
        }
    }, [template, editMode]);

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };

            // Handle bodyPart changes
            if (field === 'bodyPart') {
                if (value === 'FULL_BODY') {
                    // Reset secondary muscles when switching to FULL_BODY
                    newData.secondaryMuscle = undefined;
                    newData.secondaryMuscle2 = undefined;
                }
            }

            return newData;
        });

        // Clear error when user starts typing/selecting
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: false
            }));
        }
    };

    const handleGoalToggle = (goalValue) => {
        setFormData(prev => ({
            ...prev,
            goals: prev.goals.includes(goalValue)
                ? prev.goals.filter(g => g !== goalValue)
                : [...prev.goals, goalValue]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        // `name` (the legacy English column) is NOT validated any more — the user does not type it.
        // English is an entity_translation row written through the translation panel; `name` is
        // derived in handleSave because new_templates.name is NOT NULL.
        if (!formData.nameHeb.trim()) newErrors.nameHeb = true;
        if (!formData.gender) newErrors.gender = true;
        if (!formData.experienceLevel) newErrors.experienceLevel = true;
        if (!formData.bodyPart) newErrors.bodyPart = true;
        if (formData.goals.length === 0) newErrors.goals = true;
        // Upper bound as well as lower: the server rejects anything past MAX_* with a 400, and a
        // template saved at 40x40 expands into a program tree big enough to break archiving.
        if (formData.numberOfBlocks < MIN_BLOCKS || formData.numberOfBlocks > MAX_BLOCKS) {
            newErrors.numberOfBlocks = true;
        }
        if (formData.numberOfWeeksInBlock < MIN_WEEKS_IN_BLOCK
            || formData.numberOfWeeksInBlock > MAX_WEEKS_IN_BLOCK) {
            newErrors.numberOfWeeksInBlock = true;
        }

        // Training Focus validation
        if (formData.bodyPart === 'CUSTOM' && !formData.secondaryMuscle) {
            newErrors.secondaryMuscle = true;
        }

        // Company assignment is OPTIONAL for a group admin: a program can be created privately in their
        // library and assigned to authorized companies later from the edit flow — so no minimum here.

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        console.log(`${editMode ? 'Edit' : 'Create'} was pressed, with data: `, formData);
        if (validateForm()) {
            // Clean up the data before saving
            const cleanedData = { ...formData };

            // `new_templates.name` AND `name_heb` are both NOT NULL, and `name` is no longer a form
            // field. On EDIT pass the loaded English through untouched — rewriting it from Hebrew
            // would corrupt the English an installed mobile binary still reads (it sends no
            // Accept-Language, so it gets the raw column rather than a resolved translation). On
            // CREATE there is nothing to pass through and the column cannot be null, so it takes
            // the Hebrew name, which is exactly the state Phase 3 converges on.
            cleanedData.name = (formData.name || '').trim() || formData.nameHeb.trim();

            // Convert empty strings to null for consistency (except for required fields).
            //
            // The warmup fields are exempt in EDIT mode only. There, '' means the coach deliberately
            // cleared the warmup text (the mobile app then hides it), and sending null instead would
            // read as "not set" — which the server's create-time default would silently refill,
            // making the field impossible to clear. In CREATE mode '' -> null is exactly what we
            // want: it lets the server seed the standard copy for a brand-new template.
            const preserveEmpty = editMode
                ? ['name', 'nameHeb', 'warmupText', 'warmupTextHeb']
                : ['name', 'nameHeb'];
            Object.keys(cleanedData).forEach(key => {
                if (cleanedData[key] === '' && !preserveEmpty.includes(key)) {
                    cleanedData[key] = null;
                }
            });

            // Only a group admin carries companyPermissions on create — leave the super-admin /
            // company-admin payload untouched (empty [] would mean "no companies" to those flows).
            if (isGroupAdmin) {
                cleanedData.companyPermissions = groupAdminCompanies;
            }

            onSave(cleanedData);
        }
    };

    const handleClose = () => {
        // Reset form only if we're in create mode
        if (!editMode) {
            setFormData(getInitialFormData());
            setGroupAdminCompanies([]);
        }
        setErrors({});
        onClose();
    };

    // Check if secondary muscle fields should be disabled
    const isSecondaryMuscleDisabled = formData.bodyPart === 'FULL_BODY';

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                    direction: 'rtl'
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderBottom: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px'
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: '18px',
                        color: '#1E293B',
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    {editMode ? 'עריכת תוכנית אימון' : 'יצירת תוכנית אימון חדשה'}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{ color: '#64748B' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: '#FFFFFF',
                    padding: '24px',
                    direction: 'rtl'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Company assignment — group admins only. The program is created in the group
                        admin's own library and shared with the chosen authorized companies. */}
                    {isGroupAdmin && (
                        <>
                            <Box>
                                <GroupAdminCompaniesField
                                    value={groupAdminCompanies}
                                    onChange={(companyPermissions) => {
                                        setGroupAdminCompanies(companyPermissions);
                                        if (companyPermissions.length > 0 && errors.companyPermissions) {
                                            setErrors(prev => ({...prev, companyPermissions: false}));
                                        }
                                    }}
                                    error={!!errors.companyPermissions}
                                />
                            </Box>
                            <Divider />
                        </>
                    )}

                    {/* Basic Information */}
                    <Box>
                        <Typography
                            sx={{
                                marginTop: "8px",
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1E293B',
                                marginBottom: 2,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מידע בסיסי
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        label="שם התוכנית (עברית)"
                                        helperText={editMode
                                            ? "שמות וטקסטים בשפות אחרות נערכים בחלון התרגומים"
                                            : "ניתן להוסיף תרגומים לאחר יצירת התוכנית"}
                                        value={formData.nameHeb}
                                        onChange={(e) => handleInputChange('nameHeb', e.target.value)}
                                        error={errors.nameHeb}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#FFFFFF',
                                                '& fieldset': { borderColor: errors.nameHeb ? '#EF4444' : '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: errors.nameHeb ? '#EF4444' : '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: errors.nameHeb ? '#EF4444' : '#64748B' }
                                            }
                                        }}
                                    />
                                </RTL>
                            </Grid>

                            <Grid item xs={12}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="תיאור (עברית)"
                                        value={formData.descriptionHeb}
                                        onChange={(e) => handleInputChange('descriptionHeb', e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#64748B' }
                                            }
                                        }}
                                    />
                                </RTL>
                            </Grid>

                            <Grid item xs={12}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="טקסט חימום (עברית)"
                                        helperText="מוצג למתאמן בראש מסך האימון. השינוי חל רק על תוכניות שיוקצו מעכשיו — תוכנית שכבר הוקצתה נערכת במסך המתאמן."
                                        value={formData.warmupTextHeb}
                                        onChange={(e) => handleInputChange('warmupTextHeb', e.target.value)}
                                        inputProps={{ maxLength: 4000 }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E2E8F0' },
                                                '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                '&.Mui-focused fieldset': { borderColor: '#64748B' }
                                            }
                                        }}
                                    />
                                </RTL>
                            </Grid>

                        </Grid>

                        {/* Every language other than the canonical Hebrew, rendered from
                            GET /admin/translation/schema/NEW_TEMPLATE. In CREATE mode there is no
                            row to attach a translation to yet, so the panel says so rather than
                            buffering writes it cannot address — the template is saved first, then
                            translated from this same dialog. */}
                        <Box sx={{ mt: 2 }}>
                            <TranslationDialogButton
                                type="NEW_TEMPLATE"
                                entityId={editMode ? (formData.id ?? null) : null}
                                fieldLabels={NEW_TEMPLATE_FIELD_LABELS}
                                entityNoun="התוכנית"
                                dialogTitle="תרגומי התוכנית"
                            />
                        </Box>
                    </Box>

                    <Divider />

                    {/* Target Audience */}
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1E293B',
                                marginBottom: 2,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            קהל יעד
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <RTL>
                                    <FormControl fullWidth error={errors.gender}>
                                        <InputLabel>מגדר</InputLabel>
                                        <Select
                                            value={formData.gender}
                                            onChange={(e) => handleInputChange('gender', e.target.value)}
                                            label="מגדר"
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.gender ? '#EF4444' : '#E2E8F0'
                                                }
                                            }}
                                        >
                                            {genderOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.nameHeb}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <RTL>
                                    <FormControl fullWidth error={errors.experienceLevel}>
                                        <InputLabel>רמת ניסיון</InputLabel>
                                        <Select
                                            value={formData.experienceLevel}
                                            onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                                            label="רמת ניסיון"
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.experienceLevel ? '#EF4444' : '#E2E8F0'
                                                }
                                            }}
                                        >
                                            {experienceLevels.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider />

                    {/* Training Focus */}
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1E293B',
                                marginBottom: 2,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מיקוד אימון *
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <RTL>
                                    <FormControl fullWidth error={errors.bodyPart}>
                                        <InputLabel>חלק גוף ראשי *</InputLabel>
                                        <Select
                                            value={formData.bodyPart}
                                            onChange={(e) => handleInputChange('bodyPart', e.target.value)}
                                            label="חלק גוף ראשי *"
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.bodyPart ? '#EF4444' : '#E2E8F0'
                                                }
                                            }}
                                        >
                                            {bodyParts.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <RTL>
                                    <FormControl
                                        fullWidth
                                        error={errors.secondaryMuscle}
                                        disabled={isSecondaryMuscleDisabled}
                                    >
                                        <InputLabel>
                                            {formData.bodyPart === 'CUSTOM' ? 'שריר משני *' : 'שריר משני'}
                                        </InputLabel>
                                        <Select
                                            value={isSecondaryMuscleDisabled ? '' : formData.secondaryMuscle || ''}
                                            onChange={(e) => handleInputChange('secondaryMuscle', e.target.value)}
                                            label={formData.bodyPart === 'CUSTOM' ? 'שריר משני *' : 'שריר משני'}
                                            sx={{
                                                backgroundColor: isSecondaryMuscleDisabled ? '#F1F5F9' : '#FFFFFF',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.secondaryMuscle ? '#EF4444' : '#E2E8F0'
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ fontSize: '14px' }}>ללא</Typography>
                                            </MenuItem>
                                            {SecondaryMuscles.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <RTL>
                                    <FormControl
                                        fullWidth
                                        disabled={isSecondaryMuscleDisabled}
                                    >
                                        <InputLabel>שריר משני נוסף</InputLabel>
                                        <Select
                                            value={isSecondaryMuscleDisabled ? '' : formData.secondaryMuscle2 || ''}
                                            onChange={(e) => handleInputChange('secondaryMuscle2', e.target.value)}
                                            label="שריר משני נוסף"
                                            sx={{
                                                backgroundColor: isSecondaryMuscleDisabled ? '#F1F5F9' : '#FFFFFF'
                                            }}
                                        >
                                            <MenuItem value="">
                                                <Typography sx={{ fontSize: '14px' }}>ללא</Typography>
                                            </MenuItem>
                                            {SecondaryMuscles.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>
                        </Grid>

                        {/* Helper text */}
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: '#64748B',
                                marginTop: 1,
                                fontStyle: 'italic'
                            }}
                        >
                            {formData.bodyPart === 'FULL_BODY' &&
                                'בחירת "כללי כל גוף" לא מאפשרת בחירת שרירים משניים'
                            }
                            {formData.bodyPart === 'CUSTOM' &&
                                'בחירת "מותאם אישית" מחייבת בחירת לפחות שריר משני אחד'
                            }
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Training Goals */}
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1E293B',
                                marginBottom: 2,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מטרות האימון
                            {errors.goals && (
                                <Typography
                                    component="span"
                                    sx={{ color: '#EF4444', fontSize: '12px', marginRight: 1 }}
                                >
                                    (חובה לבחור לפחות מטרה אחת)
                                </Typography>
                            )}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(TemplateGoals).map(([value, nameHeb]) => (
                                <Chip
                                    key={value}
                                    label={nameHeb}
                                    onClick={() => handleGoalToggle(value)}
                                    variant={formData.goals.includes(value) ? "filled" : "outlined"}
                                    sx={{
                                        backgroundColor: formData.goals.includes(value) ? '#64748B' : 'transparent',
                                        color: formData.goals.includes(value) ? '#FFFFFF' : '#64748B',
                                        borderColor: errors.goals ? '#EF4444' : '#E2E8F0',
                                        '&:hover': {
                                            backgroundColor: formData.goals.includes(value) ? '#475569' : '#F8FAFC'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Divider />

                    {/* Training Structure */}
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1E293B',
                                marginBottom: 2,
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מבנה האימון
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <RTL>
                                    <FormControl fullWidth>
                                        <InputLabel>סוג ציוד</InputLabel>
                                        <Select
                                            value={formData.equipmentType}
                                            onChange={(e) => handleInputChange('equipmentType', e.target.value)}
                                            label="סוג ציוד"
                                            sx={{ backgroundColor: '#FFFFFF' }}
                                        >
                                            {EquipmentTypes.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    <Typography sx={{ fontSize: '14px' }}>
                                                        {option.name}
                                                    </Typography>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="מספר בלוקים"
                                        value={formData.numberOfBlocks}
                                        onChange={(e) => handlePeriodizationChange(
                                            'numberOfBlocks', e.target.value, MAX_BLOCKS)}
                                        inputProps={{ min: MIN_BLOCKS, max: MAX_BLOCKS }}
                                        sx={{ backgroundColor: '#FFFFFF' }}
                                        error={errors.numberOfBlocks || clamped.numberOfBlocks}
                                        helperText={
                                            errors.numberOfBlocks ? PERIODIZATION_ERROR_TEXT
                                                : clamped.numberOfBlocks ? maxExceededText(MAX_BLOCKS)
                                                    : ''
                                        }
                                    />
                                </RTL>
                            </Grid>

                            <Grid item xs={12} sm={3}>
                                <RTL>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="שבועות בבלוק"
                                        value={formData.numberOfWeeksInBlock}
                                        onChange={(e) => handlePeriodizationChange(
                                            'numberOfWeeksInBlock', e.target.value, MAX_WEEKS_IN_BLOCK)}
                                        inputProps={{ min: MIN_WEEKS_IN_BLOCK, max: MAX_WEEKS_IN_BLOCK }}
                                        sx={{ backgroundColor: '#FFFFFF' }}
                                        error={errors.numberOfWeeksInBlock || clamped.numberOfWeeksInBlock}
                                        helperText={
                                            errors.numberOfWeeksInBlock ? PERIODIZATION_ERROR_TEXT
                                                : clamped.numberOfWeeksInBlock ? maxExceededText(MAX_WEEKS_IN_BLOCK)
                                                    : ''
                                        }
                                    />
                                </RTL>
                            </Grid>
                        </Grid>
                    </Box>

                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    backgroundColor: '#FCFCFD',
                    borderTop: '1px solid #E2E8F0',
                    padding: '16px 24px',
                    direction: 'rtl'
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{
                        color: 'secondary',
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    ביטול
                </Button>
                <RTL>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        startIcon={editMode ? <SaveIcon /> : <AddIcon />}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: '#FFFFFF',
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            '&:hover': {
                                backgroundColor: '#475569'
                            }
                        }}
                    >
                        {editMode ? 'שמירת שינויים' : 'יצירת תוכנית'}
                    </Button>
                </RTL>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTemplateDialog;